import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { SignalDoc } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";

import { filterDismissibleDocIds } from "./persistentSignals";
import { signalHref } from "./signalHref";

type SignalsContextValue = {
  signals: SignalDoc[];
  unreadSignals: SignalDoc[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
  markRead: (docIds: SignalDoc["docId"][]) => void;
  autoMarkRead: (docIds: SignalDoc["docId"][]) => void;
};

const SignalsContext = createContext<SignalsContextValue>({
  signals: [],
  unreadSignals: [],
  isLoading: false,
  isError: false,
  refetch: () => Promise.resolve(),
  markRead: () => undefined,
  autoMarkRead: () => undefined,
});

const isSignalPageActive = (signal: SignalDoc, pathname: string): boolean => {
  const href = signalHref(signal);
  if (!href) return false;
  return pathname === href || pathname.startsWith(`${href}/`);
};

const signalsQueryKey = (userKey: string | undefined, limit: number) =>
  ["docs", "searchSignals", userKey, { limit }] as const;

export const SignalsProvider: React.FC<{
  children: React.ReactNode;
  isAuthLoaded: boolean;
}> = ({ children, isAuthLoaded }) => {
  const location = useLocation();
  const auth = useContext(IamAuthContext);
  const userKey = auth.iamUser?.id;
  const signalsLimit = 1000;

  const query = useClientManager("docs")
    .searchSignals()
    .useQuery(
      { limit: signalsLimit },
      {
        enabled: isAuthLoaded && Boolean(userKey),
        queryKey: [...signalsQueryKey(userKey, signalsLimit)],
        refetchInterval: isAuthLoaded && Boolean(userKey) ? 5_000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
      },
    );

  const setReadMutation = useClientManager("docs")
    .setSignalRead()
    .useMutation({
      onSuccess: () => {
        void query.refetch();
      },
    });

  const signals = useMemo((): SignalDoc[] => query.data?.items ?? [], [query.data?.items]);

  const unreadSignals = useMemo(() => signals.filter((signal) => !signal.isRead), [signals]);

  const markRead = useCallback(
    (docIds: SignalDoc["docId"][]) => {
      if (docIds.length === 0 || setReadMutation.isLoading) return;
      setReadMutation.mutate({ docIds, isRead: true });
    },
    [setReadMutation],
  );

  const autoMarkRead = useCallback(
    (docIds: SignalDoc["docId"][]) => {
      markRead(filterDismissibleDocIds(docIds, signals));
    },
    [markRead, signals],
  );

  useEffect(() => {
    const docIds = signals
      .filter((signal) => !signal.isRead && isSignalPageActive(signal, location.pathname))
      .map((signal) => signal.docId);

    autoMarkRead(docIds);
  }, [location.pathname, autoMarkRead, signals]);

  return (
    <SignalsContext.Provider
      value={{
        signals,
        unreadSignals,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch,
        markRead,
        autoMarkRead,
      }}
    >
      {children}
    </SignalsContext.Provider>
  );
};

export const useSignals = (): SignalsContextValue => useContext(SignalsContext);
